/**
 * @architecture-header 1
 * @layer module
 * @domain live
 * @responsibility Send intervention messages, record resolutions, and end a run with confirmation.
 * @state-owner TanStack Query mutation state
 * @allowed-imports app environment; TanStack Query; contracts schemas
 * @forbidden routes; components; direct generated SDK; realtime channel storage; reconstructing server-side keys; client-asserted roles
 * @public-api live module only
 * @side-effects authenticated message, resolution and run-end commands
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests modules/live/model/agent-policy.test.ts, modules/live/store/seat-alerts.test.ts and module-contracts.test.ts
 */

import { useMutation } from '@tanstack/react-query';
import { type RecallPreview, recallPreviewSchema, recallResultSchema } from '@vispo/contracts';
import { useWorkspaceEnvironment } from '../../../app/config/environment';

/**
 * The conversation is identified by the path, so the client sends only what it alone knows: the
 * text, and an id that lets a retry be recognised as the same message rather than a second one.
 * It does not name the conversation and it does not declare who is speaking — the first is a
 * server-side key the client should never have to reproduce, and the second is already established
 * by the access token.
 */
export function useSendInterventionMessage() {
  const environment = useWorkspaceEnvironment();
  return useMutation({
    mutationFn: async ({
      classId,
      studentId,
      message,
    }: {
      classId: string;
      studentId: string;
      message: string;
    }) => {
      if (environment.demoMode) return { id: `message-${Date.now()}`, message };
      return environment.apiClient.request<{ id: string; content: string }>(
        `/api/classes/${classId}/students/${studentId}/messages`,
        {
          method: 'POST',
          body: { content: message, clientMessageId: crypto.randomUUID() },
        },
      );
    },
  });
}

export function useResolveIntervention() {
  const environment = useWorkspaceEnvironment();
  return useMutation({
    mutationFn: async ({ classId, studentId }: { classId: string; studentId: string }) => {
      if (environment.demoMode) return { resolvedAt: new Date().toISOString() };
      return environment.apiClient.request<{ resolvedAt: string }>(
        `/api/classes/${classId}/students/${studentId}/interventions/resolution`,
        { method: 'POST' },
      );
    },
  });
}

export function useEndRun() {
  const environment = useWorkspaceEnvironment();
  return useMutation({
    mutationFn: async ({
      confirm,
      itemId,
    }: {
      confirm: boolean;
      itemId: string;
    }): Promise<
      | { ended: false; preview: RecallPreview }
      | { ended: true; message: string; preview?: RecallPreview }
    > => {
      if (environment.demoMode) return { ended: true, message: '課堂已結束' };
      const preview = recallPreviewSchema.parse(
        await environment.apiClient.request<unknown>(
          `/api/runs/${encodeURIComponent(itemId)}/end-preview`,
        ),
      );
      if (preview.requires_confirmation && !confirm) return { ended: false, preview };
      const result = recallResultSchema.parse(
        await environment.apiClient.request<unknown>(
          `/api/runs/${encodeURIComponent(itemId)}/end`,
          {
            method: 'POST',
            body: {
              force: confirm,
              confirmSubmitted: confirm && preview.submitted_count + preview.graded_count > 0,
            },
          },
        ),
      );
      return { ended: true, message: result.message, preview };
    },
  });
}
